import { createSelector } from 'reselect';
import type { RootState } from '../store';
import { logger } from '../../helpers/logger';
import type { CountriesSettings } from './helpers.d';
import { getStore } from '../storeAccessor';

const getCountriesSettingsState = (state: RootState) => {
  const countriesSettings = (state as any)?.CountriesSettings?.countriesSettings;

  return countriesSettings;
};

// Memoized selector for countriesSettings
// This selector will only recalculate if the CountriesSettings slice changes
const countriesSettingsSelector = createSelector([getCountriesSettingsState], countriesSettings => countriesSettings);

let cachedCountriesSettings: CountriesSettings | null = null;
let unsubscribe: (() => void) | null = null;

const initializeSubscription = () => {
  if (unsubscribe) {
    return true;
  }

  const store = getStore();

  if (!store) {
    return false;
  }

  try {
    unsubscribe = store.subscribe(() => {
      try {
        const state = store.getState();
        const newCountriesSettings = countriesSettingsSelector(state);

        if (newCountriesSettings !== cachedCountriesSettings) {
          cachedCountriesSettings = newCountriesSettings;
        }
      } catch (error) {
        logger.warn(`Error in store subscription: ${error}`);
      }
    });

    return true;
  } catch (error) {
    logger.warn(`Failed to initialize store subscription: ${error}`);

    return false;
  }
};

try {
  const initialized = initializeSubscription();

  if (!initialized) {
    // If initialization failed, try again after a short delay (when store should be ready)
    setTimeout(() => {
      initializeSubscription();
    }, 0);
  }
} catch (error) {
  // Defer initialization to next event loop tick
  setTimeout(() => {
    initializeSubscription();
  }, 0);
}

// Getter function to access cached countriesSettings using memoized selector
export const getCachedCountriesSettings = (): CountriesSettings | null => {
  if (!unsubscribe) {
    try {
      const initialized = initializeSubscription();

      if (initialized) {
        const store = getStore();

        if (!store) {
          return cachedCountriesSettings;
        }

        try {
          const state = store.getState();
          const currentValue = countriesSettingsSelector(state);

          if (currentValue !== cachedCountriesSettings) {
            cachedCountriesSettings = currentValue;
          }
        } catch (error) {
          // Error getting value after initialization
        }
      }
    } catch (error) {
      return cachedCountriesSettings;
    }
  }

  return cachedCountriesSettings;
};
